/**
 * Post-onboarding Security Setup screen.
 *
 * Shown to the user immediately AFTER they finish the first-login password
 * change. Two purposes:
 *   1. (Required mode) `?required=2fa` — the company has a mandatory-2FA
 *      policy and this user doesn't yet have it. They must enable 2FA before
 *      reaching the main app.
 *   2. (Voluntary mode) shown once to brand-new users so they discover
 *      2FA + biometrics exist. They can dismiss with "Skip for now" and
 *      the flag is stored locally so they aren't nagged again.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TwoFactorAuth from '../components/TwoFactorAuth';
import BiometricAuth from '../components/BiometricAuth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Smartphone, Fingerprint, CheckCircle2, ArrowRight } from 'lucide-react';

export default function SecuritySetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, token, company } = useAuth();
  const requiredMode = params.get('required') === '2fa';
  const [view, setView] = useState('overview');   // overview | 2fa | biometric

  // If the user enabled 2FA from inside the embedded TwoFactorAuth component,
  // the user object in context still says two_factor_enabled=false until they
  // next sign in. We listen to a window event that TwoFactorAuth fires on
  // successful setup so we can flip the dismiss-button state immediately.
  const [twofaJustEnabled, setTwofaJustEnabled] = useState(false);
  useEffect(() => {
    const handler = () => setTwofaJustEnabled(true);
    window.addEventListener('2fa-enabled', handler);
    return () => window.removeEventListener('2fa-enabled', handler);
  }, []);

  const has2FA = user?.two_factor_enabled || twofaJustEnabled;
  const canSkip = !requiredMode || has2FA;

  const finish = () => {
    // Remember the user has seen this screen so we don't re-prompt voluntarily.
    if (user?.id) {
      try { localStorage.setItem(`security_setup_seen_${user.id}`, '1'); }
      catch { /* localStorage may be disabled (Safari private mode) — non-fatal */ }
    }
    navigate('/clock-in');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: '#0B1321' }}>
      <div className="w-full max-w-2xl">
        {view === 'overview' && (
          <Card className="border-0 shadow-2xl" style={{ background: '#0F1923' }}>
            <CardContent className="p-8 space-y-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: 'rgba(139, 92, 246, 0.15)' }}
                >
                  <Shield className="w-6 h-6" style={{ color: '#A78BFA' }} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    {requiredMode ? 'Set up 2FA to continue' : 'Secure your account'}
                  </h1>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>
                    {requiredMode
                      ? 'Your company requires two-factor authentication. Choose how to set it up below.'
                      : 'Add an extra layer of protection — takes about a minute.'}
                  </p>
                </div>
              </div>

              {/* Option 1 — Authenticator app */}
              <button
                onClick={() => setView('2fa')}
                className="w-full text-left p-5 rounded-xl transition-all hover:translate-y-[-2px]"
                style={{
                  background: '#0B1321',
                  border: `1px solid ${has2FA ? 'rgba(16, 185, 129, 0.4)' : '#2D3748'}`,
                }}
                data-testid="security-setup-2fa-card"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(30, 136, 229, 0.15)' }}
                  >
                    <Smartphone className="w-5 h-5" style={{ color: '#1E88E5' }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">Authenticator app</h3>
                      {has2FA && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#86EFAC' }}>
                          <CheckCircle2 className="w-3 h-3" /> Enabled
                        </span>
                      )}
                    </div>
                    <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                      Works with Google Authenticator, Microsoft Authenticator, Authy, 1Password and Apple Passwords.
                      Recommended — covers you on every device.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 mt-1" style={{ color: '#64748B' }} />
                </div>
              </button>

              {/* Option 2 — Biometric */}
              <button
                onClick={() => setView('biometric')}
                className="w-full text-left p-5 rounded-xl transition-all hover:translate-y-[-2px]"
                style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                data-testid="security-setup-biometric-card"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(0, 200, 150, 0.15)' }}
                  >
                    <Fingerprint className="w-5 h-5" style={{ color: '#00C896' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Biometric sign-in</h3>
                    <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                      Use Face ID, Touch ID or Windows Hello on this device to sign in without typing your password.
                      Fast for repeated use — pair with 2FA for best protection.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 mt-1" style={{ color: '#64748B' }} />
                </div>
              </button>

              <div className="flex items-center justify-between pt-2">
                {canSkip ? (
                  <Button
                    onClick={finish}
                    className="text-white"
                    style={{ background: has2FA ? '#10B981' : '#475569' }}
                    data-testid="security-setup-finish-btn"
                  >
                    {has2FA ? 'Continue to app' : 'Skip for now'}
                  </Button>
                ) : (
                  <span className="text-xs" style={{ color: '#FCA5A5' }}>
                    ⚠ Your company requires 2FA — finish setup above to continue
                  </span>
                )}
                <span className="text-xs" style={{ color: '#64748B' }}>
                  You can change these later from <strong>Settings → Security</strong>.
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {view === '2fa' && (
          <div className="space-y-4">
            <button
              onClick={() => setView('overview')}
              className="text-sm" style={{ color: '#94A3B8' }}
              data-testid="security-setup-back-2fa"
            >
              ← Back
            </button>
            <TwoFactorAuth user={user} token={token} company={company} />
          </div>
        )}

        {view === 'biometric' && (
          <div className="space-y-4">
            <button
              onClick={() => setView('overview')}
              className="text-sm" style={{ color: '#94A3B8' }}
              data-testid="security-setup-back-biometric"
            >
              ← Back
            </button>
            <BiometricAuth user={user} token={token} />
          </div>
        )}
      </div>
    </div>
  );
}
